import { useState } from 'react';
import { Alert, Button, Card, Form, Input } from 'antd';
import { getApiErrorMessage, serverFetch } from '@api/server';
import { useAuth } from '@context/auth-context';
import './auth.css';

export const startAuth = (email) => {
  return serverFetch('auth/start', {
    method: 'POST',
    bodyData: { email },
  });
};

export const login = (uuid, code) => {
  return serverFetch('auth/login', {
    method: 'POST',
    bodyData: { uuid, code },
  });
};

export const checkSession = () => serverFetch('auth/checksession');

export const logout = () => {
  return serverFetch('auth/logout', {
    method: 'POST',
  });
};

const Auth = () => {
  const { login: authorize } = useAuth();
  const [authUuid, setAuthUuid] = useState(null);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleStart = async (values) => {
    setError('');
    setIsSubmitting(true);
    try {
      const result = await startAuth(values.email);
      setEmail(values.email);
      setAuthUuid(result.uuid);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogin = async (values) => {
    setError('');
    setIsSubmitting(true);
    try {
      await authorize({ uuid: authUuid, code: values.code });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-aurora" aria-hidden="true" />
      <div className="login-grid" aria-hidden="true" />
      <div className="login-shell">
        <div className="login-brand">
          <div className="login-brand-mark" aria-hidden="true">
            <svg viewBox="0 0 32 32" fill="none">
              <path
                d="M9.2 13.4c0-3.8 3-6.9 6.8-6.9s6.8 3.1 6.8 6.9v6.2c0 1.3-1 2.3-2.3 2.3-1.2 0-2.2-1-2.2-2.3v-5.2c0-.6.5-1.1 1.1-1.1.5 0 1 .3 1.3.7.1-2.6-2-4.7-4.7-4.7s-4.8 2.1-4.7 4.7c.3-.4.8-.7 1.3-.7.6 0 1.1.5 1.1 1.1v5.2c0 1.3-1 2.3-2.2 2.3-1.3 0-2.3-1-2.3-2.3v-6.2z"
                fill="#06201c"
              />
              <circle cx="16" cy="24.4" r="1.7" fill="#06201c" />
            </svg>
          </div>
          <div className="login-logo">Киберменеджер</div>
          <div className="login-tagline">Ваш сотрудник 24/7</div>
        </div>
        <Card className="login-card" bordered={false}>
          <div className="login-title">{authUuid ? 'Код из письма' : 'Вход в систему'}</div>
          <p className="login-lead">
            {authUuid
              ? 'Введите шестизначный код, чтобы открыть рабочее пространство.'
              : 'Укажите почту — пришлём одноразовый код для входа.'}
          </p>
          {error && (
            <Alert className="login-alert" type="error" message={error} showIcon />
          )}
          {!authUuid ? (
            <Form className="login-form" onFinish={handleStart} requiredMark={false}>
              <Form.Item
                name="email"
                rules={[
                  { required: true, message: 'Введите email.' },
                  { type: 'email', message: 'Введите корректный email.' },
                ]}
              >
                <Input placeholder="Почта" size="large" autoFocus />
              </Form.Item>
              <Button htmlType="submit" type="primary" size="large" loading={isSubmitting} block>
                Отправить код
              </Button>
            </Form>
          ) : (
            <Form className="login-form" onFinish={handleLogin} requiredMark={false}>
              <div className="login-hint">
                Код отправлен на {email}
              </div>
              <Form.Item
                name="code"
                rules={[
                  { required: true, message: 'Введите код авторизации.' },
                  { len: 6, message: 'Код должен состоять из 6 цифр.' },
                ]}
              >
                <Input placeholder="000000" maxLength={6} size="large" autoFocus />
              </Form.Item>
              <Button htmlType="submit" type="primary" size="large" loading={isSubmitting} block>
                Войти
              </Button>
              <Button
                className="login-secondary-action"
                type="link"
                onClick={() => {
                  setAuthUuid(null);
                  setError('');
                }}
                block
              >
                Изменить email
              </Button>
            </Form>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Auth;
